import { CloseOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Form, Input, message, Modal, Select, Spin, Upload } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { BANNER_STATUS_OPTIONS, BANNER_TYPES, BannerTypeEnum } from 'constants/banner'
import { ALLOWED_MIME_TYPES_IMAGE } from 'constants/mime-type'
import useUploadFile from 'hooks/use-upload-file'
import { logError } from 'utils/logger'

interface BannerModalProps {
  open: boolean
  id: string | null
  onCancel: () => void
  onSuccess: () => void
}

const BannerModal = ({ open, id, onCancel, onSuccess }: BannerModalProps) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [imageUrl, setImageUrl] = useState<string>('')

  const isEditMode = !!id

  const { singleFile, handleUploadSingle, beforeUploadSingle, handleRemoveFile } = useUploadFile({
    mimeTypes: ALLOWED_MIME_TYPES_IMAGE,
    onUploadSuccess: (data) => {
      const filePath = data.filePath
      setImageUrl(filePath)
      form.setFieldsValue({ image: filePath })
    },
    onBeforeUpload: () => {
      message.error('You can only upload JPG/PNG/GIF/BMP/WEBP file!')
    },
  })

  const fetchBannerDetail = useCallback(
    async (bannerId: string) => {
      try {
        setFetching(true)
        const res = await AppAdmin.getDetailBanner(bannerId)
        if (res?.data.success) {
          const banner = res.data.data
          form.setFieldsValue({
            name: banner.name,
            image: banner.image,
            link: banner.link,
            type: banner.type,
            isActive: banner.isActive,
          })
          setImageUrl(banner.image || '')
        } else {
          message.error(res?.data.message || 'Failed to get banner details')
          onCancel()
        }
      } catch (error) {
        logError('Error: ', error)
        message.error('An error occurred while fetching banner details')
        onCancel()
      } finally {
        setFetching(false)
      }
    },
    [form, onCancel]
  )

  useEffect(() => {
    if (open) {
      if (id) {
        fetchBannerDetail(id)
      } else {
        form.resetFields()
        form.setFieldsValue({
          type: BannerTypeEnum.HOME,
          isActive: false,
        })
        setImageUrl('')
      }
    }
  }, [open, id, form, fetchBannerDetail])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      if (!isEditMode) {
        const res = await AppAdmin.createBanner(values)
        if (res?.data.success) {
          message.success('Banner created successfully')
          onSuccess()
        } else {
          message.error(res?.data.message || 'Failed to create banner')
        }
      } else if (id) {
        const res = await AppAdmin.updateBanner(id, values)
        if (res?.data.success) {
          message.success('Banner updated successfully')
          onSuccess()
        } else {
          message.error(res?.data.message || 'Failed to update banner')
        }
      }
    } catch (error) {
      logError('Error: ', error)
      message.error('An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveImage = () => {
    setImageUrl('')
    form.setFieldsValue({ image: '' })
    handleRemoveFile()
  }

  return (
    <Modal
      title={isEditMode ? 'Edit Banner' : 'Create Banner'}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditMode ? 'Update' : 'Create'}
      className="banner-modal"
      width={600}
    >
      <Spin spinning={fetching}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter banner name' }]}
          >
            <Input placeholder="Enter banner name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select banner type' }]}
          >
            <Select options={BANNER_TYPES} placeholder="Select banner type" />
          </Form.Item>

          <Form.Item name="link" label="Link">
            <Input placeholder="Enter link URL (optional)" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select options={BANNER_STATUS_OPTIONS} placeholder="Select status" />
          </Form.Item>

          <Form.Item
            name="image"
            label="Image"
            rules={[{ required: true, message: 'Please upload an image' }]}
          >
            <Input type="hidden" style={{ display: 'none' }} />
          </Form.Item>

          <div style={{ marginBottom: 16, marginTop: -16 }}>
            {imageUrl ? (
              <div
                className="banner-preview"
                style={{ position: 'relative', display: 'inline-block' }}
              >
                <img src={imageUrl} alt="preview" />
                <CloseOutlined
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: '#000',
                    fontSize: '14px',
                    cursor: 'pointer',
                    background: 'white',
                    borderRadius: '50%',
                    padding: '4px',
                    border: '1px solid #d9d9d9',
                  }}
                  onClick={handleRemoveImage}
                />
              </div>
            ) : (
              <Upload
                fileList={singleFile}
                customRequest={handleUploadSingle}
                beforeUpload={beforeUploadSingle}
                accept={ALLOWED_MIME_TYPES_IMAGE.join(',')}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>Upload Image</Button>
              </Upload>
            )}
            <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
              Supported formats: JPG, PNG, GIF, BMP, WEBP
            </p>
          </div>
        </Form>
      </Spin>
    </Modal>
  )
}

export default BannerModal
